import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { AutoLoading } from 'app/core/decorators/AutoLoading.decorator';
import { Pagination } from 'app/core/models/pagination.model';
import { Physician } from 'app/core/models/physician.model';
import { User } from 'app/core/models/user.model';
import { DefaultResponse } from 'app/shared/types';
import { environment } from 'environments/environment';
import { catchError, EMPTY, Observable } from 'rxjs';
import {
  PaymentLinkRequest,
  PaymentLinkResponse,
} from '../dtos/payment-link.dto';
import { PhysicianProcedure } from '../../../core/dtos/physician.dto';
import { SearchNearPhysiciansByAddressRequestDTO } from '../dtos/search-near-physicians-by-adress.dto';
import { AppointmentPurchaseDetailsResponseDTO } from '../dtos/purchase-details.dto';
import { AppointmentCandidateStatus } from 'app/core/models/appointment-candidate.model';
import { ListAppointmentsResponseDTO } from '../dtos/list-appointment-response.dto';
import { ListAppointmentsRequestDTO } from '../dtos/list-appointment-request.dto';
import { AppointmentUpdateRequestDTO } from '../dtos/appointment-update-request.dto';
import { AppointmentResponseDTO } from '../dtos/appointment-response.dto';
import { CreateAppointmentRequestDTO } from '../dtos/create-appointment-request.dto';

export abstract class ICouponApiService {
  abstract findUserByCPF(cpf: string): Observable<DefaultResponse<User>>;
  abstract findPhysicianProcedures(input: {
    physicianId: string;
    branchCode: string;
  }): Observable<
    DefaultResponse<{
      procedures: PhysicianProcedure[];
      specialties: string[];
    }>
  >;
  abstract searchNearPhysiciansByAddress(
    paramsDTO: SearchNearPhysiciansByAddressRequestDTO,
  ): Observable<DefaultResponse<Pagination<Physician>>>;
  abstract generatePaymentLink(
    payload: PaymentLinkRequest,
  ): Observable<DefaultResponse<PaymentLinkResponse>>;
  abstract listAppointments(
    params: ListAppointmentsRequestDTO,
  ): Observable<DefaultResponse<ListAppointmentsResponseDTO>>;
  abstract getAppointmentPurchaseDetails(
    appointmentId: string,
  ): Observable<DefaultResponse<AppointmentPurchaseDetailsResponseDTO>>;
  abstract updateAppointment(
    appointmentId: string,
    data: AppointmentUpdateRequestDTO,
  ): Observable<DefaultResponse<AppointmentResponseDTO>>;
  abstract sendPaymentLink(
    appointmentId: string,
  ): Observable<DefaultResponse<void>>;
  abstract cancelAppointment(
    appointmentId: string,
  ): Observable<DefaultResponse<void>>;
  abstract listPhysiciansCandidatesByUserId(input: {
    customerId: string;
    latitude?: number;
    longitude?: number;
    status?: AppointmentCandidateStatus[];
  }): Observable<DefaultResponse<Physician[]>>;
}

@Injectable({ providedIn: 'root' })
export class CouponApiService implements ICouponApiService {
  urlAPI = environment.apiUrl + '/v1';
  injector = inject(Injector);

  constructor(private readonly httpClient: HttpClient) {}

  @AutoLoading()
  public findUserByCPF(cpf: string): Observable<DefaultResponse<User>> {
    return this.httpClient.get<DefaultResponse<User>>(
      `${this.urlAPI}/backoffice/user/${cpf}/coupons`,
    );
  }

  @AutoLoading()
  public searchNearPhysiciansByAddress(
    paramsDTO: SearchNearPhysiciansByAddressRequestDTO,
  ): Observable<DefaultResponse<Pagination<Physician>>> {
    const httpParams = new HttpParams({ fromObject: { ...paramsDTO } });

    return this.httpClient.get<DefaultResponse<Pagination<Physician>>>(
      `${this.urlAPI}/physicians/by-address`,
      { params: httpParams },
    );
  }

  @AutoLoading()
  public findPhysicianProcedures(params: {
    physicianId: string;
    branchCode: string;
  }) {
    return this.httpClient
      .get<
        DefaultResponse<{
          procedures: PhysicianProcedure[];
          specialties: string[];
        }>
      >(`${environment.apiUrl}/physicians/procedures/search`, {
        params: new HttpParams({ fromObject: params }),
      })
      .pipe(catchError(() => EMPTY));
  }

  @AutoLoading()
  public generatePaymentLink(
    payload: PaymentLinkRequest,
  ): Observable<DefaultResponse<PaymentLinkResponse>> {
    return this.httpClient.post<DefaultResponse<PaymentLinkResponse>>(
      `${this.urlAPI}/backoffice/payment-link`,
      payload,
    );
  }

  @AutoLoading()
  public listAppointments(
    params: ListAppointmentsRequestDTO,
  ): Observable<DefaultResponse<ListAppointmentsResponseDTO>> {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach(item => {
              httpParams = httpParams.append(key, item.toString());
            });
          }
        } else {
          httpParams = httpParams.append(key, value.toString());
        }
      }
    });

    return this.httpClient.get<DefaultResponse<ListAppointmentsResponseDTO>>(
      `${this.urlAPI}/appointment`,
      { params: httpParams },
    );
  }

  @AutoLoading()
  public getAppointmentPurchaseDetails(
    appointmentId: string,
  ): Observable<DefaultResponse<AppointmentPurchaseDetailsResponseDTO>> {
    return this.httpClient.get<
      DefaultResponse<AppointmentPurchaseDetailsResponseDTO>
    >(`${this.urlAPI}/backoffice/appointment/${appointmentId}/purchase-detail`);
  }

  @AutoLoading()
  public updateAppointment(
    appointmentId: string,
    data: AppointmentUpdateRequestDTO,
  ): Observable<DefaultResponse<AppointmentResponseDTO>> {
    return this.httpClient.patch<DefaultResponse<AppointmentResponseDTO>>(
      `${this.urlAPI}/appointment/${appointmentId}`,
      data,
    );
  }

  @AutoLoading()
  public sendPaymentLink(
    appointmentId: string,
  ): Observable<DefaultResponse<void>> {
    return this.httpClient.post<DefaultResponse<void>>(
      `${this.urlAPI}/backoffice/appointment/${appointmentId}/send-payment-link`,
      {},
    );
  }

  @AutoLoading()
  public cancelAppointment(
    appointmentId: string,
  ): Observable<DefaultResponse<void>> {
    return this.httpClient.patch<DefaultResponse<void>>(
      `${this.urlAPI}/backoffice/appointment/${appointmentId}/cancel`,
      {},
    );
  }

  @AutoLoading()
  public listPhysiciansCandidatesByUserId(input: {
    customerId: string;
    status?: AppointmentCandidateStatus[];
    latitude?: number;
    longitude?: number;
  }): Observable<DefaultResponse<Physician[]>> {
    const httpParams = new HttpParams({ fromObject: input });

    return this.httpClient.get<DefaultResponse<Physician[]>>(
      `${this.urlAPI}/appointment-candidate/physicians`,
      { params: httpParams },
    );
  }

  @AutoLoading()
  public createAppointment(
    payload: CreateAppointmentRequestDTO,
  ): Observable<DefaultResponse<{ appointmentId: string }>> {
    return this.httpClient.post<DefaultResponse<{ appointmentId: string }>>(
      `${this.urlAPI}/backoffice/appointment`,
      payload,
    );
  }
}
